import { useEffect, useState } from 'react';

// Window Share — homepage section (SingleCombo)
// Dependency-free. Styles are scoped with the "htf-" prefix.

const ORDER = 40;
const COMMISSION_RATE = 0.25;
const SHARE_RATE = 0.02;
const fmt = (n) => `$${n.toFixed(2)}`;

const STEPS = [
  {
    title: 'They visit you',
    body: 'A customer opens your store page on SingleCombo and browses your menu.',
  },
  {
    title: 'They order elsewhere',
    body: 'Later, they place an order with a different Enterprise partner store.',
  },
  {
    title: 'You earn 2% of our commission',
    body: 'Your traffic counts, even when the sale lands somewhere else.',
  },
];

// Feature status label. Set the `status` prop to "construction", "beta",
// "launching", or null (hides the label once the feature is live).
// Optional `launchDate` (e.g. "Nov 2026") turns "Launching soon" into "Launching Nov 2026".
const STATUS = {
  construction: {
    label: 'In construction',
    note: "We're still building this. Payouts start once it goes live.",
  },
  beta: { label: 'Beta', note: 'This feature is in beta. Details may change before full launch.' },
  launching: {
    label: 'Launching soon',
    note: "This feature isn't live yet.",
  },
};

const TIERS = [
  { plan: 'Basic', min: '3,000+' },
  { plan: 'Enterprise', min: '1,500+' },
];

const RULES = [
  'Your store meets the subscriber minimum for your plan.',
  'The customer visited your store page before ordering.',
  'The order was placed with an Enterprise (Partner Delivery) store.',
];

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = (e) => setReduced(e.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

function Storefront({ x, awning, label }) {
  return (
    <g transform={`translate(${x} 170)`}>
      <rect x='0' y='22' width='120' height='70' fill='#fff' stroke='#14213D' strokeWidth='2.5' />
      <path d='M-6 22 h132 l-10 -22 h-112z' fill={awning} />
      <path d='M10 22 q10 12 20 0 q10 12 20 0 q10 12 20 0 q10 12 20 0 q10 12 20 0' fill={awning} />
      <rect x='44' y='52' width='32' height='40' fill='#F6F5F2' stroke='#14213D' strokeWidth='2' />
      <text x='60' y='44' textAnchor='middle' fontSize='12.5' fontWeight='700' fill='#14213D'>
        {label}
      </text>
    </g>
  );
}

function Illustration() {
  const reduced = useReducedMotion();
  const backPath = 'M380 272 C 340 318, 205 318, 165 272';
  return (
    <svg
      viewBox='20 72 480 254'
      className='htf-art'
      role='img'
      aria-label="A customer views Store One, then orders from Store Two, an Enterprise store. Two percent of SingleCombo's commission flows back to Store One."
    >
      <rect x='0' y='262' width='520' height='2' fill='#E6E2DC' />
      <path
        d='M60 250 C 80 170, 120 160, 135 175'
        fill='none'
        stroke='#14213D'
        strokeWidth='2.5'
        strokeDasharray='2 8'
        strokeLinecap='round'
      />
      <path
        d='M175 140 C 240 60, 330 60, 380 140'
        fill='none'
        stroke='#14213D'
        strokeWidth='2.5'
        strokeDasharray='2 8'
        strokeLinecap='round'
      />

      <path d={backPath} fill='none' stroke='#F26A1B' strokeWidth='3' strokeLinecap='round' />
      <path
        d='M174 272 l-10 -1 l1 10'
        fill='none'
        stroke='#F26A1B'
        strokeWidth='3'
        strokeLinecap='round'
        strokeLinejoin='round'
      />

      <rect x='216' y='296' width='88' height='26' rx='13' fill='#FDE8DA' />
      <text x='260' y='313.5' textAnchor='middle' fontSize='13' fontWeight='700' fill='#A8420A'>
        2% back
      </text>

      <g transform={reduced ? 'translate(330 296)' : undefined}>
        <circle r='12' fill='#F26A1B' />
        <text y='4.5' textAnchor='middle' fontSize='12' fontWeight='700' fill='#fff'>
          %
        </text>
        {!reduced && (
          <animateMotion
            dur='3.2s'
            repeatCount='indefinite'
            path={backPath}
            keyPoints='0;0;1'
            keyTimes='0;0.4;1'
            calcMode='linear'
          />
        )}
      </g>

      <circle cx='55' cy='222' r='11' fill='#14213D' />
      <path d='M38 262 q17 -32 34 0z' fill='#14213D' />

      <Storefront x={105} awning='#14213D' label='Store One' />
      <Storefront x={320} awning='#F26A1B' label='Store Two' />

      <g transform='translate(165 120)'>
        <circle r='22' fill='#fff' stroke='#14213D' strokeWidth='2' />
        <path d='M-12 0 q12 -12 24 0 q-12 12 -24 0z' fill='none' stroke='#14213D' strokeWidth='2' />
        <circle r='4' fill='#14213D' />
      </g>
      <text x='165' y='88' textAnchor='middle' fontSize='12' fill='#5B6275'>
        Viewed
      </text>

      <g transform='translate(380 120)'>
        <circle r='22' fill='#fff' stroke='#14213D' strokeWidth='2' />
        <path d='M-9 -6 h18 l2 18 h-22z' fill='#F26A1B' />
        <path d='M-9 -6 h18 v-4 h-18z' fill='#14213D' />
      </g>
      <text x='380' y='88' textAnchor='middle' fontSize='12' fill='#5B6275'>
        Ordered
      </text>

      <rect x='402' y='148' width='74' height='20' rx='10' fill='#14213D' />
      <text x='439' y='162' textAnchor='middle' fontSize='11' fontWeight='500' fill='#fff'>
        Enterprise
      </text>
    </svg>
  );
}

export default function WindowSharingRevenue({ status = 'launching', launchDate }) {
  const badge = STATUS[status];
  const commission = ORDER * COMMISSION_RATE;
  const share = commission * SHARE_RATE;

  return (
    <section className='htf-wrap' aria-labelledby='htf-title'>
      <style>{css}</style>
      <div className='htf'>
        <div className='htf-hero'>
          <div>
            <div className='htf-tags'>
              <span className='htf-tag'>Window Share</span>
              {badge && (
                <span className={`htf-badge htf-badge--${status}`}>
                  {status === 'launching' && launchDate ? `Launching ${launchDate}` : badge.label}
                </span>
              )}
            </div>
            <h2 id='htf-title' className='htf-h1'>
              They looked at your menu. You still get paid.
            </h2>
            <p className='htf-lead'>
              When a customer browses your store but orders from another Enterprise store, we share
              2% of our commission on that order with you.
            </p>
          </div>
          <Illustration />
        </div>

        <ol className='htf-steps'>
          {STEPS.map((s, i) => (
            <li key={s.title} className='htf-step'>
              <span className={`htf-num${i === STEPS.length - 1 ? ' is-last' : ''}`}>{i + 1}</span>
              <div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className='htf-example'>
          <div>
            <h3 className='htf-h2'>The other store pays nothing extra.</h3>
            <p>
              Your share comes out of SingleCombo's commission, so no partner is ever charged for
              someone else's earnings. It's how we reward the stores that bring customers in.
            </p>
          </div>
          <dl className='htf-receipt' aria-label='Example payout'>
            <div>
              <dt>Order at Store Two</dt>
              <dd>{fmt(ORDER)}</dd>
            </div>
            <div>
              <dt>Our commission ({COMMISSION_RATE * 100}%)</dt>
              <dd>{fmt(commission)}</dd>
            </div>
            <div className='htf-total'>
              <dt>Store One earns</dt>
              <dd>{fmt(share)}</dd>
            </div>
          </dl>
        </div>

        <div className='htf-rules'>
          <div>
            <h3 className='htf-h2 htf-navy'>Who qualifies</h3>
            <div className='htf-tiers'>
              {TIERS.map((t) => (
                <div key={t.plan} className='htf-tier'>
                  <span>{t.plan} account</span>
                  <strong>{t.min}</strong>
                  <small>subscribers</small>
                </div>
              ))}
            </div>
          </div>
          <ul>
            {RULES.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>

        <p className='htf-fine'>
          {badge && <strong className='htf-fine-status'>{badge.note} </strong>}
          Once it launches, you can start earning after your store meets the subscriber minimum and
          you accept the Window Share terms. Your share is 2% of SingleCombo's commission on each
          qualifying order. Window Share earnings are kept separate from your regular order payouts
          and are paid out only when you request them. Program terms apply.
        </p>
      </div>
    </section>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&display=swap');
.htf-wrap{width:100%;margin-top:20px;border-top:2px solid #000;background:#F8F8F8}
.htf{--o:#F26A1B;--os:#FDE8DA;--n:#14213D;--ink:#1E2433;--mu:#5B6275;--ln:#E6E2DC;
  font-family:Poppins,system-ui,sans-serif;color:var(--ink);background:transparent;line-height:1.55;
  max-width:1040px;margin:0 auto;border-radius:28px;overflow:hidden}
.htf *{box-sizing:border-box;margin:0;padding:0}
.htf-hero{display:grid;grid-template-columns:1fr 1.1fr;gap:32px;align-items:center;padding:56px}
.htf-tags{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-bottom:16px}
.htf-tag{display:inline-block;background:var(--os);color:#A8420A;font-weight:500;font-size:14px;padding:6px 12px;border-radius:8px}
.htf-badge{display:inline-flex;align-items:center;gap:7px;font-weight:700;font-size:14px;padding:6px 12px;border-radius:8px;color:#fff;background:var(--n)}
.htf-badge::before{content:"";width:8px;height:8px;border-radius:50%;background:currentColor}
.htf-badge--launching{background:var(--o)}
.htf-badge--beta{background:var(--n)}
.htf-badge--beta::before{background:var(--o)}
.htf-badge--construction{color:var(--n);background:repeating-linear-gradient(-45deg,#FFD9C0 0 10px,#FFE9DB 10px 20px);box-shadow:inset 0 0 0 2px var(--o)}
.htf-badge--construction::before{background:var(--o)}
.htf-tiers{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:6px}
.htf-tier{background:#fff;border:1.5px solid var(--ln);border-radius:14px;padding:14px 16px;display:flex;flex-direction:column}
.htf-tier:last-child{border-color:var(--o);background:#FFF8F3}
.htf-tier span{font-size:13px;font-weight:500;color:var(--mu)}
.htf-tier strong{font-size:28px;line-height:1.15;font-weight:700;color:var(--n)}
.htf-tier small{font-size:13px;color:var(--mu)}
.htf-fine-status{color:var(--n);font-weight:700}
.htf-h1{font-size:clamp(30px,4.4vw,50px);line-height:1.06;font-weight:700;letter-spacing:-.02em;color:var(--n)}
.htf-lead{margin-top:16px;font-size:17px;color:var(--mu);max-width:44ch}
.htf-art{width:100%;height:auto;display:block;font-family:Poppins,system-ui,sans-serif}

.htf-steps{list-style:none;display:grid;grid-template-columns:repeat(3,1fr);border-top:1px solid var(--ln)}
.htf-step{display:flex;flex-direction:column;gap:12px;padding:32px;border-right:1px solid var(--ln)}
.htf-step:last-child{border-right:0}
.htf-num{display:grid;place-items:center;width:34px;height:34px;flex:none;border-radius:50%;background:var(--n);color:#fff;font-weight:500;font-size:15px}
.htf-num.is-last{background:var(--o)}
.htf-step h3{font-size:18px;font-weight:700;color:var(--n);margin-bottom:4px}
.htf-step p{color:var(--mu);font-size:15px}

.htf-example{display:grid;grid-template-columns:1.2fr 1fr;gap:40px;align-items:center;background:var(--n);color:#fff;padding:44px 56px}
.htf-h2{font-size:24px;line-height:1.25;font-weight:700;margin-bottom:10px}
.htf-example p{color:#C4CADB;font-size:15.5px;max-width:46ch}
.htf-receipt{background:#fff;color:var(--ink);border-radius:14px;padding:18px 22px;font-size:15px}
.htf-receipt div{display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding:9px 0;border-bottom:1px dashed var(--ln)}
.htf-receipt dd{font-variant-numeric:tabular-nums}
.htf-receipt .htf-total{border:0;padding-top:12px;font-weight:700}
.htf-receipt .htf-total dd{color:var(--o);font-size:21px}

.htf-rules{display:grid;grid-template-columns:1fr 1fr;gap:40px;padding:44px 56px 28px}
.htf-navy{color:var(--n)}
.htf-rules ul{list-style:none;display:grid;gap:14px}
.htf-rules li{position:relative;padding-left:30px;font-size:15.5px}
.htf-rules li::before{content:"";position:absolute;left:0;top:4px;width:18px;height:18px;border-radius:50%;
  box-shadow:inset 0 0 0 5px var(--os),inset 0 0 0 9px var(--o)}
.htf-fine{font-size:13px;color:var(--mu);padding:0 56px 36px}

@media (max-width:820px){
  .htf{border-radius:0}
  .htf-hero{grid-template-columns:1fr;gap:24px;padding:36px 20px 28px}
  .htf-lead{font-size:16px}
  .htf-steps{grid-template-columns:1fr}
  .htf-step{flex-direction:row;align-items:flex-start;gap:14px;padding:20px;border-right:0;border-bottom:1px solid var(--ln)}
  .htf-step:last-child{border-bottom:0}
  .htf-example{grid-template-columns:1fr;gap:24px;padding:32px 20px}
  .htf-h2{font-size:21px}
  .htf-rules{grid-template-columns:1fr;gap:16px;padding:32px 20px 20px}
  .htf-fine{padding:0 20px 28px}
}
`;
